/**
 * Cookie / local-storage consent state (DPDP).
 *
 * Two categories:
 *  - essential  — always on, not toggleable (auth/session storage: Cognito, sessionStorage token)
 *  - analytics  — click/impression tracking used for "Most popular" ranking and teacher analytics
 *
 * The decision is stored in localStorage, versioned: bumping CONSENT_VERSION
 * re-prompts every visitor (do this whenever the cookie policy materially changes).
 * The current analytics decision is also sent to the backend on every API call as
 * the `X-Analytics-Consent` header (see apiFetch), so server-side event recording
 * can honor it.
 */

export const CONSENT_VERSION = 1;

const STORAGE_KEY = "bmc_cookie_consent";

/** Fired on window whenever consent changes or a re-open is requested. */
export const CONSENT_CHANGED_EVENT = "bmc:cookie-consent-changed";
export const CONSENT_REOPEN_EVENT = "bmc:cookie-consent-reopen";

export interface CookieConsent {
  version: number;
  /** ISO timestamp of the decision — kept for a future backend audit trail. */
  updatedAt: string;
  analytics: boolean;
}

export function getConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    // A version bump invalidates the stored decision — re-prompt.
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent(analytics: boolean): CookieConsent {
  const consent: CookieConsent = {
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    analytics,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Storage unavailable (private mode quota etc.) — the banner will re-show.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));
  return consent;
}

/** Whether analytics tracking is allowed. Undecided = false (opt-in, per DPDP). */
export function hasAnalyticsConsent(): boolean {
  return getConsent()?.analytics === true;
}

/**
 * Consent signal for the backend, attached to every API request.
 * "pending" = the visitor has not decided yet (backend should treat as denied).
 */
export function consentHeaderValue(): "granted" | "denied" | "pending" {
  const c = getConsent();
  if (!c) return "pending";
  return c.analytics ? "granted" : "denied";
}

/** Re-opens the consent banner (e.g. from a "Cookie preferences" footer link). */
export function reopenConsentBanner(): void {
  window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
}
